import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Gamepad2, Loader2 } from "lucide-react";
import { LiveStats } from "@/components/LiveStats";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { authenticate } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(path, body);
      authenticate(data.user);
      toast.success(mode === "login" ? "Welcome back, player!" : "Account created — press start!");
      navigate("/home");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A14] flex items-center justify-center px-6 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-20"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1511512578047-dfb367046420?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70')", backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-[#0A0A14]/80" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <Gamepad2 className="w-8 h-8 text-[#00F0FF]" />
          <span className="font-head font-900 text-2xl text-slate-50">INNER<span className="text-[#A78BFA]">ARCADE</span></span>
        </Link>

        <div className="flex justify-center mb-6">
          <LiveStats />
        </div>

        <div className="bg-[#11111A] border border-cyan-500/20 p-8 shadow-[8px_8px_0px_rgba(0,240,255,0.1)]">
          <div className="flex gap-2 mb-8">
            <button
              data-testid="tab-login"
              onClick={() => setMode("login")}
              className={`flex-1 font-arcade text-lg py-2 border transition-colors duration-200 ${mode === "login" ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "border-cyan-500/30 text-slate-400 hover:text-slate-200"}`}
            >LOG IN</button>
            <button
              data-testid="tab-register"
              onClick={() => setMode("register")}
              className={`flex-1 font-arcade text-lg py-2 border transition-colors duration-200 ${mode === "register" ? "bg-[#FF006E] text-white border-[#FF006E]" : "border-cyan-500/30 text-slate-400 hover:text-slate-200"}`}
            >SIGN UP</button>
          </div>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="font-arcade text-base text-[#00F0FF] block mb-1.5">GAMER TAG</label>
                <input
                  data-testid="name-input"
                  value={name} onChange={(e) => setName(e.target.value)} required
                  className="w-full bg-[#0A0A14] border border-slate-700 px-4 py-2.5 text-slate-100 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-colors duration-200"
                  placeholder="PixelWarrior"
                />
              </div>
            )}
            <div>
              <label className="font-arcade text-base text-[#00F0FF] block mb-1.5">EMAIL</label>
              <input
                data-testid="email-input" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full bg-[#0A0A14] border border-slate-700 px-4 py-2.5 text-slate-100 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-colors duration-200"
                placeholder="you@arcade.io"
              />
            </div>
            <div>
              <label className="font-arcade text-base text-[#00F0FF] block mb-1.5">PASSWORD</label>
              <input
                data-testid="password-input" type="password"
                value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full bg-[#0A0A14] border border-slate-700 px-4 py-2.5 text-slate-100 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-colors duration-200"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="auth-submit-btn"
              type="submit" disabled={loading}
              className="w-full font-arcade text-xl py-3 bg-[#F5C842] text-black border border-[#F5C842] hover:shadow-[4px_4px_0px_#FF006E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (mode === "login" ? "PRESS START" : "CREATE ACCOUNT")}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-slate-700" />
            <span className="font-arcade text-sm text-slate-500">OR</span>
            <div className="h-px flex-1 bg-slate-700" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            data-testid="google-signin-btn"
            type="button"
            onClick={() => {
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full font-arcade text-lg py-3 bg-white text-black border border-white hover:shadow-[4px_4px_0px_#00F0FF] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200 flex items-center justify-center gap-3"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            CONTINUE WITH GOOGLE
          </button>
        </div>
        <p className="text-center text-xs text-slate-500 mt-6">Demo admin: admin@arcade.io / admin123</p>
      </div>
    </div>
  );
}
